export type Audience = "municipality" | "business" | "individual";

export type AudienceOption = {
  id: Audience;
  code: string;
  label: string;
  title: string;
  description: string;
  promise: string;
};

export type NeedKind = "Tovary" | "Služby" | "Stavebné práce" | "Komplexný dopyt";

export type NeedCategory = {
  id: string;
  code: string;
  title: string;
  kind: NeedKind;
  description: string;
  examples: string[];
  audiences: Audience[];
  tone: "green" | "blue" | "copper" | "plum" | "teal" | "red";
};

export type ProcurementDraft = {
  audience: Audience;
  categoryId: string;
  title: string;
  description: string;
  clientType: string;
  clientName: string;
  ico: string;
  region: string;
  contactName: string;
  email: string;
  phone: string;
  deliveryPlace: string;
  budgetBand: string;
  estimatedValue: string;
  deadline: string;
  fundingSource: string;
  priorities: string[];
  requirements: string;
  hasSpecification: boolean;
  needsMarketResearch: boolean;
  needsFullProcess: boolean;
  expertSupport: string[];
  communicationChannels: string[];
  fileNames: string[];
  confirmation: boolean;
};

export type AgentEmployee = {
  code: string;
  title: string;
  autonomy: "Autonómny" | "Kontrolovaný" | "Schvaľovaný";
  capability: string;
};

export type AgentTeam = {
  id: string;
  name: string;
  mission: string;
  agents: AgentEmployee[];
};

export type WorkflowStage = {
  id: string;
  label: string;
  title: string;
  description: string;
  owner: string;
};

export const audienceOptions: AudienceOption[] = [
  {
    id: "municipality",
    code: "VO",
    label: "Samospráva",
    title: "Verejný nákup pod kontrolou",
    description: "Pre obce, mestá, kraje, školy a organizácie verejného sektora.",
    promise: "ZVO, PHZ, dokumentácia, súťaž, zmluva a plnenie s povinnými rozhodovacími bránami.",
  },
  {
    id: "business",
    code: "B2B",
    label: "Firma",
    title: "Firemné obstarávanie bez chaosu",
    description: "Pre firmy, prevádzky, developerov, správcov a profesionálne tímy.",
    promise: "Dopyt, špecifikácia, porovnanie ponúk, vyjednanie, zmluva a kontrola dodania.",
  },
  {
    id: "individual",
    code: "YOU",
    label: "Jednotlivec",
    title: "Marketplace pre zložité rozhodnutia",
    description: "Pre ľudí, ktorí nechcú sami hľadať, porovnávať a koordinovať dodávateľov.",
    promise: "Jednoduchý dopyt, dôveryhodný výber, zabezpečenie nákupu, služby a dodania.",
  },
];

export const emptyDraft: ProcurementDraft = {
  audience: "municipality",
  categoryId: "",
  title: "",
  description: "",
  clientType: "",
  clientName: "",
  ico: "",
  region: "",
  contactName: "",
  email: "",
  phone: "",
  deliveryPlace: "",
  budgetBand: "",
  estimatedValue: "",
  deadline: "",
  fundingSource: "",
  priorities: [],
  requirements: "",
  hasSpecification: false,
  needsMarketResearch: true,
  needsFullProcess: true,
  expertSupport: [],
  communicationChannels: ["Písomne"],
  fileNames: [],
  confirmation: false,
};

export const needCategories: NeedCategory[] = [
  {
    id: "equipment",
    code: "01",
    title: "Vybavenie a produkty",
    kind: "Tovary",
    description: "Technika, nábytok, pracovné vybavenie, prevádzkové zásoby alebo osobný nákup.",
    examples: ["mobiliár", "stroje", "vybavenie domácnosti"],
    audiences: ["municipality", "business", "individual"],
    tone: "green",
  },
  {
    id: "digital",
    code: "02",
    title: "IT a digitalizácia",
    kind: "Služby",
    description: "Hardvér, softvér, licencie, kybernetická bezpečnosť a elektronické služby.",
    examples: ["informačný systém", "počítače", "licencie a podpora"],
    audiences: ["municipality", "business", "individual"],
    tone: "blue",
  },
  {
    id: "energy-operations",
    code: "03",
    title: "Energia a technická prevádzka",
    kind: "Služby",
    description: "Energie, servis budov, osvetlenie, údržba a technické zabezpečenie.",
    examples: ["servis technológií", "energetický audit", "údržba"],
    audiences: ["municipality", "business"],
    tone: "teal",
  },
  {
    id: "expert-services",
    code: "04",
    title: "Odborné a projektové služby",
    kind: "Služby",
    description: "Projektovanie, audity, analýzy, vzdelávanie, právo a špecializované poradenstvo.",
    examples: ["projektová dokumentácia", "audit", "odborný posudok"],
    audiences: ["municipality", "business", "individual"],
    tone: "plum",
  },
  {
    id: "construction",
    code: "05",
    title: "Stavby, rekonštrukcie a bývanie",
    kind: "Stavebné práce",
    description: "Budovy, komunikácie, rekonštrukcie, interiéry a drobné stavebné zásahy.",
    examples: ["rekonštrukcia školy", "kancelária", "obnova domu alebo bytu"],
    audiences: ["municipality", "business", "individual"],
    tone: "red",
  },
  {
    id: "mobility",
    code: "06",
    title: "Mobilita a doprava",
    kind: "Komplexný dopyt",
    description: "Vozidlá, preprava, logistika, zdieľaná mobilita a súvisiaci servis.",
    examples: ["služobné vozidlo", "preprava", "logistika a doručenie"],
    audiences: ["municipality", "business", "individual"],
    tone: "copper",
  },
  {
    id: "personal-marketplace",
    code: "07",
    title: "Osobný marketplace dopyt",
    kind: "Komplexný dopyt",
    description: "Čokoľvek hodnotnejšie alebo zložitejšie, čo chcete bezpečne vybrať a zabezpečiť.",
    examples: ["technológia", "remeselník", "sťahovanie alebo väčší nákup"],
    audiences: ["individual"],
    tone: "copper",
  },
  {
    id: "custom",
    code: "08",
    title: "Iná alebo kombinovaná potreba",
    kind: "Komplexný dopyt",
    description: "Potreba presahuje jednu kategóriu alebo ešte nemá presnú špecifikáciu.",
    examples: ["viac dodávateľov", "návrh aj realizácia", "neštandardný projekt"],
    audiences: ["municipality", "business", "individual"],
    tone: "green",
  },
];

export const clientTypes: Record<Audience, string[]> = {
  municipality: [
    "Obec",
    "Mesto alebo mestská časť",
    "Samosprávny kraj",
    "Rozpočtová alebo príspevková organizácia",
    "Škola alebo školské zariadenie",
    "Mestský podnik alebo iná verejná organizácia",
  ],
  business: [
    "Živnostník alebo malá firma",
    "Stredná firma",
    "Veľká spoločnosť alebo skupina",
    "Developer alebo správca nehnuteľností",
    "Nezisková organizácia",
  ],
  individual: ["Súkromná osoba", "Rodina alebo domácnosť", "Skupina vlastníkov"],
};

export const regions = [
  "Bratislavský kraj",
  "Trnavský kraj",
  "Trenčiansky kraj",
  "Nitriansky kraj",
  "Žilinský kraj",
  "Banskobystrický kraj",
  "Prešovský kraj",
  "Košický kraj",
  "Celé Slovensko alebo viac krajín",
];

export const budgetBands = [
  "Do 2 000 EUR",
  "2 000 - 10 000 EUR",
  "10 000 - 50 000 EUR",
  "50 000 - 180 000 EUR",
  "180 000 - 500 000 EUR",
  "Nad 500 000 EUR",
  "Zatiaľ nevieme",
];

export const fundingSources: Record<Audience, string[]> = {
  municipality: [
    "Vlastný rozpočet",
    "Dotácia zo štátneho rozpočtu",
    "Európske fondy alebo Plán obnovy",
    "Kombinované financovanie",
    "Zdroj ešte nie je potvrdený",
  ],
  business: ["Vlastné zdroje", "Úver alebo leasing", "Grant alebo dotácia", "Kombinované financovanie"],
  individual: ["Vlastné zdroje", "Úver alebo financovanie", "Rozpočet ešte nie je potvrdený"],
};

export const priorityOptions = [
  "Najnižšie celkové náklady",
  "Kvalita a životnosť",
  "Rýchlosť dodania",
  "Environmentálne kritériá",
  "Lokálny servis a dostupnosť",
  "Minimálne prevádzkové riziko",
];

export const expertOptions = [
  "Právnik",
  "Verejný obstarávateľ",
  "Projektant alebo inžinier",
  "Rozpočtár alebo znalec",
  "Bezpečnostný konzultant",
  "Metodická konzultácia s úradom",
];

export const communicationOptions = ["Písomne", "Hlasový AI agent", "Videohovor", "Bezpečný dátový priestor"];

export const agentTeams: AgentTeam[] = [
  {
    id: "command",
    name: "Case Command",
    mission: "Rozkladá zadanie, prideľuje prácu, riadi termíny, závislosti a eskalácie.",
    agents: [
      { code: "OR-01", title: "Orchestrátor prípadu", autonomy: "Autonómny", capability: "Plán, úlohy a koordinácia tímov" },
      { code: "IN-02", title: "Intake analytik", autonomy: "Autonómny", capability: "Doplňujúce otázky a kontrola úplnosti" },
      { code: "SL-03", title: "SLA kontrolór", autonomy: "Autonómny", capability: "Termíny, upozornenia a eskalácie" },
    ],
  },
  {
    id: "law",
    name: "Law & Public Compliance",
    mission: "Pripravuje zákonný rámec a kontrolné protokoly; rozhodnutie a právny záväzok neschvaľuje.",
    agents: [
      { code: "ZV-11", title: "ZVO analytik", autonomy: "Kontrolovaný", capability: "Režim, princípy, lehoty a dokumentácia" },
      { code: "RG-12", title: "Regulačný monitor", autonomy: "Autonómny", capability: "Aktuálne znenia, limity a metodika" },
      { code: "CO-13", title: "Konflikt a férovosť", autonomy: "Schvaľovaný", capability: "Konflikt záujmov a rovnaké zaobchádzanie" },
    ],
  },
  {
    id: "engineering",
    name: "Specification & Engineering",
    mission: "Premieňa potrebu na neutrálny, merateľný a dodateľný opis.",
    agents: [
      { code: "SP-21", title: "Špecifikačný architekt", autonomy: "Kontrolovaný", capability: "Opis predmetu a požiadavky" },
      { code: "EX-22", title: "Koordinátor expertov", autonomy: "Autonómny", capability: "Právnici, projektanti a konzultanti" },
      { code: "AC-23", title: "Akceptačný inžinier", autonomy: "Kontrolovaný", capability: "Testy, kvalita a prevzatie" },
    ],
  },
  {
    id: "market",
    name: "Market Intelligence",
    mission: "Mapuje trh, pripravuje PHZ alebo cenový benchmark a porovnáva ponuky.",
    agents: [
      { code: "SC-31", title: "Supplier Scout", autonomy: "Autonómny", capability: "Vyhľadanie a preverenie dodávateľov" },
      { code: "PH-32", title: "PHZ a cenový analytik", autonomy: "Kontrolovaný", capability: "Trhové dáta a cenové porovnanie" },
      { code: "BI-33", title: "Bid Intelligence", autonomy: "Schvaľovaný", capability: "Porovnanie ponúk bez finálneho rozhodnutia" },
    ],
  },
  {
    id: "communications",
    name: "Communications & Liaison",
    mission: "Vedie označenú AI komunikáciu písomne aj hlasovo a zabezpečuje súčinnosť expertov a úradov.",
    agents: [
      { code: "VO-41", title: "Hlasový liaison", autonomy: "Kontrolovaný", capability: "Označené hovory, zápis a následné úlohy" },
      { code: "WR-42", title: "Písomný liaison", autonomy: "Autonómny", capability: "E-maily, otázky, odpovede a statusy" },
      { code: "AU-43", title: "Authority liaison", autonomy: "Schvaľovaný", capability: "Podklady pre úrady a metodické otázky" },
    ],
  },
  {
    id: "finance",
    name: "Finance & Contract",
    mission: "Stráži rozpočet, financovanie, zmluvné riziká a platobné podmienky.",
    agents: [
      { code: "FN-51", title: "Finančný analytik", autonomy: "Kontrolovaný", capability: "Rozpočet, TCO a zdroj financovania" },
      { code: "CT-52", title: "Contract Builder", autonomy: "Schvaľovaný", capability: "Návrh zmluvy a rizikových klauzúl" },
      { code: "IV-53", title: "Fakturačný kontrolór", autonomy: "Autonómny", capability: "Súlad faktúr, míľnikov a plnenia" },
    ],
  },
  {
    id: "delivery",
    name: "Delivery & Quality",
    mission: "Riadi plnenie od objednávky po prevzatie, reklamáciu a referenciu.",
    agents: [
      { code: "DL-61", title: "Delivery Controller", autonomy: "Autonómny", capability: "Míľniky, logistika a potvrdenia" },
      { code: "QA-62", title: "Quality Assurance", autonomy: "Kontrolovaný", capability: "Kontrola kvality a akceptácie" },
      { code: "CL-63", title: "Claims Manager", autonomy: "Schvaľovaný", capability: "Reklamácie, náprava a zmluvné nároky" },
    ],
  },
  {
    id: "trust",
    name: "Trust, Privacy & Security",
    mission: "Vynucuje diskrétnosť, minimálne oprávnenia, ochranu údajov a úplnú auditnú stopu.",
    agents: [
      { code: "DP-71", title: "Data Protection Guard", autonomy: "Schvaľovaný", capability: "GDPR, DPIA, minimalizácia a retencia" },
      { code: "CY-72", title: "Cybersecurity Guard", autonomy: "Autonómny", capability: "Prístupy, šifrovanie, incidenty a izolácia" },
      { code: "AI-73", title: "AI Governance Auditor", autonomy: "Kontrolovaný", capability: "AI Act, transparentnosť, logy a hodnotenie" },
    ],
  },
];

export const workflowStages: WorkflowStage[] = [
  { id: "intake", label: "01", title: "Prevzatie potreby", description: "Cieľ, rozsah, zodpovednosti a chýbajúce podklady.", owner: "Case Command" },
  { id: "analysis", label: "02", title: "Režim a trh", description: "ZVO podľa segmentu, PHZ alebo cenový benchmark a trhová mapa.", owner: "Law + Market" },
  { id: "specification", label: "03", title: "Špecifikácia", description: "Merateľné požiadavky, kritériá, rozpočet a akceptácia.", owner: "Engineering" },
  { id: "approval", label: "04", title: "Rozhodovacia brána", description: "Človek schváli právne, finančné alebo výberové rozhodnutie.", owner: "Supervízor" },
  { id: "competition", label: "05", title: "Dopyt alebo súťaž", description: "Komunikácia, ponuky, vysvetlenia a porovnanie.", owner: "Market + Liaison" },
  { id: "contract", label: "06", title: "Zmluva a objednávka", description: "Schválenie záväzku, podpis alebo KEP a odoslanie.", owner: "Finance + človek" },
  { id: "delivery", label: "07", title: "Plnenie a dôkaz", description: "Dodanie, kvalita, faktúra, reklamácia a uzatvorenie.", owner: "Delivery & Quality" },
];

export const autonomyLanes = [
  {
    level: "GREEN",
    title: "AI vykoná samostatne",
    detail: "Zber podkladov, rešerš, plánovanie, statusy, pripomienky, zápisy a neformálne otázky bez právneho účinku.",
  },
  {
    level: "AMBER",
    title: "AI vykoná s kontrolou",
    detail: "Trhové konzultácie, hovory, technické návrhy, PHZ, citlivé správy a podklady pre expertov alebo úrady.",
  },
  {
    level: "RED",
    title: "Rozhoduje a autorizuje človek",
    detail: "Vylúčenie, výber ponuky, právny záväzok, finančný súhlas, podpis, KEP/eID, odoslanie formálneho podania a výnimka z pravidiel.",
  },
];

export const caseOutputs = [
  "Záznam o prevzatí potreby a zodpovednostiach",
  "Procesný režim alebo nákupná stratégia",
  "Prieskum trhu, PHZ alebo cenový benchmark",
  "Opis predmetu a akceptačné kritériá",
  "Dopytové alebo súťažné podklady",
  "Porovnanie ponúk s vysvetliteľným hodnotením",
  "Zmluvný, objednávkový a dodací plán",
  "Kompletná komunikácia a auditná stopa",
];
